import { useContext, useEffect, useMemo, useState, useRef, ChangeEvent } from 'react';
import {
  Box, Button, Grid, TextField, MenuItem, TableContainer, Table,
  TableHead, TableBody, TableRow, TableCell, Typography, TableSortLabel,
  SvgIcon, InputAdornment, Switch, Autocomplete, Divider, Snackbar, Alert,
  useMediaQuery, IconButton, FormControlLabel, Tooltip, useTheme,
} from '@mui/material';
import { DateField } from '@mui/x-date-pickers';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import DoneIcon from '@mui/icons-material/Done';
import { MagicWand01, Plus, SearchSm } from '../../../assets';
import { OrderKind } from '../../../enums/OrderKind';
import { useNavigate, useParams } from 'react-router-dom';
import { UserContext } from '../../../components/shared/useUser';

import { isAutoShipOrder, isOrderTypeDSD, OrderTypeTranslationMap } from '../../../enums/OrderType';
import {
  client, Category, OrderType,
  ItemsFilterOptions, Item, AddedItem, DeliverySchedule, OrderDetails, Supplier,
  CutOffTimeLeft, OrderlySystem,
} from '../../../app/services/api/orderManagementClient';
import { OrderStatus } from '../../../enums/OrderStatus';
import CustomPagination from '../../../components/forms/Pagination';
import LoadingWheel from '../../../components/ui/LoadingWheel';
import dayjs, { Dayjs } from 'dayjs';
import PageTitle from '../../../components/shared/PageTitle';
import PageArea from '../../../components/shared/PageArea';
import BackButton from '../../../components/shared/actions/BackButton';
import { useTranslation } from 'react-i18next';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';
import { isOrderCategoryParent, OrdersCategoriesParentsMap, isOrderCategory, OrderCategoriesTranslationMap } from '../../../enums/OrderCategories';
import { podClient, PredictedDemandModel, ItemConversion } from './../../../app/services/api/podClient';
import './QuickOrder.css';
import { QuantityWarningDialog } from './../../../components/shared/orders/QuantityWarningDialog';
import { isPodEnabledEnvironment } from './../../../app/services/environment';
import useCurrencyCodeConfiguration from '../../../hooks/configurations/useCurrencyCodeConfiguration';
import usePodPredictionReadonlyConfiguration from '../../../hooks/configurations/usePodPredictionReadonlyConfiguration';
import RemoveItemConfirm from './modals/RemoveItemConfirm';
import { getDateFormatByFranchise, getInventorySku, applyRoundAndFormat, calculateItemParQuantity } from '../../../utils';
import { getNext7DaysDeliverySchedules } from './../utils';
import { ProductCard } from '../../../components/shared/orders/ProductCard';

const QuickOrderPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { selectedStore, hasPermissionTo, hasPremiumModuleAccess } = useContext(UserContext);
  const canEditPredictions = hasPermissionTo(['AIOrderingAdmin', 'AIOrderingEditPredictions']);
  const { t } = useTranslation('quickOrder');
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.sm}px)`);
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const { isPodPredictionReadonlyEnabled, getPodPredictionReadonlyConfiguration } = usePodPredictionReadonlyConfiguration();

  const { id } = useParams();
  // Order type and supplier are fixed once an order exists (mirrors OrderDetails' read-only fields) - Add Items must not swap them.
  const isExistingOrder = !!id;

  const [loading, setLoading] = useState(true);
  const [productSearch, setProductSearch] = useState('');
  const [orderType, setOrderType] = useState<number>(-1);
  const [supplier, setSupplier] = useState<string>('');
  const [category, setCategory] = useState<number>(-1);
  const [deliveryDate, setDeliveryDate] = useState<Dayjs | null>(null);
  const [cutOffDate, setCutOffDate] = useState<Dayjs | null>(null);
  const [orderKind, setOrderKind] = useState(OrderKind.Forecast);

  const [page, setPage] = useState<number>(0);
  const pageReset = useRef(false);
  const [pageSize, setPageSize] = useState<number>(10);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string>('Name');
  const [totalPages, setTotalPages] = useState<number>(0);

  const [orderTypes, setOrderTypes] = useState<OrderType[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [deliverySchedules, setDeliverySchedules] = useState<DeliverySchedule[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [addedItems, setAddedItems] = useState<AddedItem[]>([]);
  const [itemQuantities, setItemQuantities] = useState<Record<number, number>>({});
  const [clearedParItemIds, setClearedParItemIds] = useState<Set<number>>(new Set());
  // Raw text of a quantity field currently being edited, so backspace/delete works normally and the field can be blank mid-edit.
  const [draftQuantityText, setDraftQuantityText] = useState<Record<number, string>>({});
  const [isPodEnabled, setIsPodEnabled] = useState(false);
  const [showPredictedQuantity, setShowPredictedQuantity] = useState<boolean>(true);
  const [isMatchPredictedQuantitiesActive, setIsMatchPredictedQuantitiesActive] = useState<boolean>(false);
  const [matchedPredictionPages, setMatchedPredictionPages] = useState<number[]>([]);
  const [predictedDemands, setPredictedDemands] = useState<PredictedDemandModel[]>([]);
  const [loadedPredictions, setLoadedPredictions] = useState<Set<string>>(new Set());

  const [showQuantityWarningDialog, setShowQuantityWarningDialog] = useState<boolean>(false);
  const [warningMessage, setWarningMessage] = useState<string>('');

  const [itemIdToRemove, setItemIdToRemove] = useState<number | undefined>();
  const [itemDescToRemove, setItemDescToRemove] = useState<string | undefined>();
  const [showItemRemovalModal, setShowItemRemovalModal] = useState<boolean>(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const dateFormat = getDateFormatByFranchise(selectedStore?.franchiseName);
  const isParEnabled = orderDetails?.isParEnabled === true;

  useEffect(() => {
    const fetchOrderTypesOnPageLoad = async () => {
      setLoading(true);
      if (!id) {
        await fetchOrderTypes(undefined);
      }
      setLoading(false);
    };

    getCurrencyCodeConfiguration();
    getPodPredictionReadonlyConfiguration();

    if (id && !isNaN(parseInt(id))) {
      loadData()
        .catch((error) => {
          handleSnackbarOpen(t('errorLoadingData' + ' ' + error), 'error');
        });
    }

    fetchOrderTypesOnPageLoad()
      .catch((error) => {
        handleSnackbarOpen(t('errorLoadingData') + ' ' + error, 'error');
      });
  }, [selectedStore, id]);

  useEffect(() => {
    if (isPodEnabledEnvironment()) {
      loadPodStoreConfiguration();
    }
  }, [selectedStore]);

  useEffect(() => {
    const fetchItemsOnPagination = async () => {
      if (!loading && orderType !== -1 && !pageReset.current) {
        setLoading(true);
        setLoadedPredictions(new Set());
        await fetchItems({
          item: productSearch,
          orderType: orderType,
          storeNumber: selectedStore?.storeNumber,
          categoryId: category,
          supplier: supplier,
          pageNumber: page,
          pageSize: pageSize,
          sort: orderBy,
          direction: order,
        });
        setLoading(false);
      }
      pageReset.current = false;
    };
    fetchItemsOnPagination()
      .catch((error) => {
        handleSnackbarOpen(t('errorLoadingData') + ' ' + error, 'error');
      });
  }, [page]);

  useEffect(() => {
    const canLoadPredictions = isPodPredictionReadonlyEnabled || hasPermissionTo(['AIOrderingAdmin', 'AIOrderingEditPredictions']);
    if (isPodEnabled && hasPremiumModuleAccess('Predictive Ordering Settings') && deliveryDate && items.length > 0 && canLoadPredictions) {
      loadPredictedDemands();
    }
  }, [isPodEnabled, isPodPredictionReadonlyEnabled, deliveryDate, items]);

  useEffect(() => {
    const loadedItems = Array.from(loadedPredictions);
    const areItemsLoaded = addedItems.map(i => getInventorySku(i.item)).every(x => loadedItems.includes(x));
    const canLoadPredictions = isPodPredictionReadonlyEnabled || hasPermissionTo(['AIOrderingAdmin', 'AIOrderingEditPredictions']);
    if (!areItemsLoaded && isPodEnabled && hasPremiumModuleAccess('Predictive Ordering Settings') && deliveryDate && !loading && canLoadPredictions) {
      loadPredictedDemands();
    }
  }, [addedItems, isPodPredictionReadonlyEnabled]);

  useEffect(() => {
    if (!matchedPredictionPages.includes(page) && isMatchPredictedQuantitiesActive) {
      setIsMatchPredictedQuantitiesActive(true);
      setMatchedPredictionPages([...matchedPredictionPages, page]);
      matchPredictedQuantities();
    }
  }, [predictedDemands]);

  const loadData = async () => {
    setLoading(true);
    await fetchOrderDetails();
    setLoading(false);
  };

  const updateOrderEditStatus = (order: OrderDetails) => {
    if (isAutoShipOrder(order.orderTypeId)) {
      return OrderStatus.Autoshipment;
    }

    return order.status;
  };

  const fetchOrderDetails = async (orderId?: number) => {
    try {
      const orderIdToFetch = orderId ?? parseInt(id ?? '');
      const result = await client.getOrder(selectedStore?.storeNumber, orderIdToFetch);
      result.data.status = updateOrderEditStatus(result.data);
      setOrderDetails(result.data);
      setOrderType(result.data.orderTypeId);
      setDeliveryDate(dayjs(result.data.deliveryDate, 'DD-MM-YYYY'));
      setCutOffDate(dayjs(result.data.cutOffDate, 'DD-MM-YYYY'));
      setOrderKind(result.data.emergencyOrder ? OrderKind.Emergency : OrderKind.Forecast);
      setSupplier(result.data.supplierNumber);
      const fetchedAddedItems = result.data.items.map(item => ({
        item: {
          id: item.itemId,
          sku: item.sku,
          name: item.name,
          categoryId: item.categoryId,
          category: item.category,
          stockOnHand: item.stockOnHand,
          stockOnOrder: item.stockOnOrder,
          price: item.price,
          primaryUom: item.primaryUom,
          orderableUom: item.orderableUom,
          barcode: '',
          barcodeUom: '',
          imageId: item.imageId,
          thumbnailUrl: item.thumbnailUrl,
          quantity: item.quantity,
          packSize: item.packSize,
          calculatedParQuantity: item.calculatedParQuantity,
          parQuantityApplied: item.parQuantityApplied,
          isQuantityOverridden: item.isQuantityOverridden,
        },
        quantity: item.quantity,
        isNew: false,
        maxQuantity: item.maxQuantity,
        minQuantity: item.minQuantity,
      }));

      setAddedItems(fetchedAddedItems);
      setClearedParItemIds(new Set());

      const newItemQuantities = result.data.items.reduce((acc, item) => ({
        ...acc,
        [item.itemId]: item.quantity,
      }), {});

      setItemQuantities(newItemQuantities);
      await fetchDeliverySchedules(result.data.orderTypeId, result.data.supplierNumber);
      await fetchOrderTypes(result.data.orderTypeId, result.data.supplierNumber);
    } catch (error) {
      handleSnackbarOpen('Error fetching order details! ' + error, 'error');
    }
  };

  const fetchItems = async (filters: ItemsFilterOptions) => {
    try {
      const itemsResponse = await client.getItems(filters);
      setItems(itemsResponse.data.items);
      setTotalPages(itemsResponse.data.totalPages);
      if (page !== itemsResponse.data.pageNumber) {
        pageReset.current = true;
        setPage(itemsResponse.data.pageNumber);
      }
    } catch (error) {
      handleSnackbarOpen(t('errorLoadingData') + ' ' + error, 'error');
    }
  };

  const fetchOrderTypes = async (defaultOrderTypeId: number | undefined, existingSupplierNumber?: string) => {
    try {
      const orderTypesResponse = await client.getOrderTypes(selectedStore?.storeNumber);
      if (orderTypesResponse.success && orderTypesResponse.data.length > 0) {
        const nonAutoshipOrderTypes = orderTypesResponse.data.filter(x => !isAutoShipOrder(x.id));
        setOrderTypes(nonAutoshipOrderTypes);
        let defaultOrderType = nonAutoshipOrderTypes.length > 0 ? nonAutoshipOrderTypes[0].id : -1;
        defaultOrderType = defaultOrderTypeId != undefined ? defaultOrderTypeId : defaultOrderType;
        setOrderType(defaultOrderType);
        const fetchedSuppliers = await fetchSuppliers();
        // Preserve the order's already-loaded supplier (e.g. editing an existing order) instead of
        // defaulting to the first supplier returned, which would silently swap the order's real supplier.
        const existingSupplierIsValid = existingSupplierNumber
          && fetchedSuppliers?.some(s => s.number === existingSupplierNumber);
        const defaultSupplier = existingSupplierIsValid
          ? existingSupplierNumber
          : (fetchedSuppliers && fetchedSuppliers.length > 0 && isOrderTypeDSD(defaultOrderType) ? fetchedSuppliers[0].number : '');
        setSupplier(defaultSupplier);
        await fetchDeliverySchedules(defaultOrderType, defaultSupplier);
        await fetchCategories(defaultOrderType, defaultSupplier);
        await fetchItems({
          item: '',
          orderType: defaultOrderType,
          storeNumber: selectedStore?.storeNumber,
          categoryId: -1,
          supplier: defaultSupplier,
          pageNumber: page,
          pageSize: pageSize,
          sort: 'Name',
          direction: 'asc',
        });
      }
    } catch (error) {
      handleSnackbarOpen(t('errorLoadingData') + ' ' + error, 'error');
    }
  };

  const fetchCategories = async (selectedOrderType: number, supplier: string) => {
    try {
      const categories = await client.getCategories(selectedStore?.storeNumber, selectedOrderType, supplier);
      setCategories(categories.data);
    } catch (error) {
      setCategories([]);
      handleSnackbarOpen(t('errorLoadingData') + ' ' + error, 'error');
    }
  };

  const fetchDeliverySchedules = async (selectedOrderType: number, supplierNumber: string) => {
    try {
      const deliverySchedulesResponse = await client.getDeliverySchedules(selectedStore?.storeNumber, selectedOrderType, supplierNumber);
      setDeliverySchedules(deliverySchedulesResponse.data || []);
    } catch (error) {
      handleSnackbarOpen(t('errorLoadingData') + ' ' + error, 'error');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const suppliers = await client.getSuppliers(selectedStore?.storeNumber);
      setSuppliers(suppliers.data);
      return suppliers.data; // Return the fetched data
    } catch (error) {
      setSuppliers([]);
      handleSnackbarOpen(t('errorLoadingData') + ' ' + error, 'error');
      return [];
    }
  };

  const loadPodStoreConfiguration = async () => {
    try {
      if (selectedStore?.franchiseName && selectedStore.storeNumber) {
        const response = await podClient.getStoreConfiguration(selectedStore.franchiseName, selectedStore.storeNumber);
        setIsPodEnabled(response.isPodEnabled);
      }
    } catch (error) {
      handleSnackbarOpen(t('errorLoadingData'), 'error');
    }
  };

  const loadPredictedDemands = async () => {
    try {
      const uniqueDates = Array.from(new Set(deliverySchedules.map(x => x.deliveryDate)));
      let deliveryDates = uniqueDates.sort((a, b) =>
        dayjs(a, 'DD-MM-YYYY').valueOf() - dayjs(b, 'DD-MM-YYYY').valueOf(),
      );

      if (orderKind === OrderKind.Emergency && deliveryDate) {
        const formattedDate = deliveryDate.format('DD-MM-YYYY');
        if (!deliveryDates.includes(formattedDate)) {
          deliveryDates = [...deliveryDates, formattedDate].sort((a, b) =>
            dayjs(a, 'DD-MM-YYYY').valueOf() - dayjs(b, 'DD-MM-YYYY').valueOf(),
          );
        }
      }

      if (selectedStore?.franchiseName && selectedStore.storeNumber) {
        const addedItemNumbers = addedItems.map(x => getInventorySku(x.item));
        const podItemNumbers = Array.from(new Set([...addedItemNumbers, ...items.map(x => getInventorySku(x))]));

        setLoadedPredictions((prev) => {
          const existingPredictions = Array.from(prev);
          return new Set([...existingPredictions, ...podItemNumbers]);
        });

        const allItems = Array.from(new Set([...items, ...addedItems.map(x => x.item)]));
        const conversions: ItemConversion[] = allItems
          .filter(item => podItemNumbers.includes(getInventorySku(item)))
          .map(item => {
            const itemWithPackSize = item as unknown as { sku: string; packSize: string; isFrozen: boolean; minQuantity?: number | null };
            const addedItem = addedItems.find(x => getInventorySku(x.item) === getInventorySku(itemWithPackSize));
            return {
              itemNumber: getInventorySku(itemWithPackSize),
              conversionNumber: typeof itemWithPackSize.packSize === 'string'
                ? parseFloat(itemWithPackSize.packSize) || 1
                : itemWithPackSize.packSize || 1,
              isFrozen: itemWithPackSize.isFrozen || false,
              minimumOrderQuantity: itemWithPackSize.minQuantity ?? addedItem?.minQuantity ?? null,
            };
          });

        const predictions = await podClient.getPredictionsForItems(
          selectedStore.franchiseName,
          selectedStore.storeNumber,
          deliveryDates,
          podItemNumbers,
          conversions,
        );
        setPredictedDemands(predictions);
      }
    } catch (error) {
      handleSnackbarOpen(t('errorLoadingData'), 'error');
    }
  };

  const handleSort = async (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setLoading(true);
    await fetchItems({
      storeNumber: selectedStore?.storeNumber,
      orderType: orderType,
      categoryId: category,
      item: productSearch,
      supplier: supplier,
      pageNumber: page,
      pageSize: pageSize,
      sort: property,
      direction: isAsc ? 'desc' : 'asc',
    });
    setLoading(false);
  };

  const handleSearch = async () => {
    setLoading(true);
    await fetchItems(getFilterOptions(true));
    await fetchCategories(orderType, supplier);
    setLoading(false);
  };

  const handleSave = async () => {
    if (!selectedStore?.storeNumber || !deliveryDate || !cutOffDate || orderType === -1) {
      return;
    }

    if (!orderDetails && orderKind === OrderKind.Emergency) {
      const itemsToSave = isParEnabled
        ? addedItems.filter(p => !clearedParItemIds.has(p.item.id))
        : addedItems;
      const createOrderRequest =
      {
        storeNumber: selectedStore.storeNumber,
        emergencyOrder: orderKind === OrderKind.Emergency,
        orderTypeId: orderType,
        deliveryDate: deliveryDate.format('YYYY-MM-DD'),
        cutOffDate: cutOffDate.format('YYYY-MM-DD'),
        supplierNumber: supplier,
        orderlySystem: OrderlySystem.OFS,
        items: itemsToSave.map(product => ({
          id: product.item.id,
          sku: product.item.sku,
          quantity: itemQuantities[product.item.id] ?? product.quantity ?? 1,
          predictedQuantity: predictedDemands.find(x => x.itemNumber === getInventorySku(product.item))
            ?.predictedDemandItems?.find(x => deliveryDate?.isSame(x.date, 'day'))?.quantity,
        })),
      };

      try {
        setLoading(true);
        const response = await client.createOrder(createOrderRequest);
        if (response.success) {
          handleSnackbarOpen(t('orderCreatedSuccessfully'), 'success');
          await fetchOrderDetails(response.data);
          navigate(`/store/quick-order/${response.data}`, { replace: true });
        }
        else {
          setShowQuantityWarningDialog(true);
          setWarningMessage(response.message);
          fixQuantities();
        }
      } catch (error) {
        // error handled by snackbar
      } finally {
        setLoading(false);
      }
    }
    else if (orderDetails && (orderKind === OrderKind.Forecast || orderKind === OrderKind.Emergency)) {
      await handleUpdate();
    }
  };

  const handleUpdate = async () => {
    if (!orderDetails) return;

    const updatedItems = Object.entries(itemQuantities).map(([itemId, quantity]) => {
      const found = addedItems?.find(x => x.item.id.toString() === itemId)?.item;
      const predictedQuantity = found
        ? predictedDemands.find(x => x.itemNumber === getInventorySku(found))
          ?.predictedDemandItems?.find(x => deliveryDate?.isSame(x.date, 'day'))?.quantity
        : undefined;
      return {
        id: parseInt(itemId),
        quantity: quantity,
        predictedQuantity,
      };
    });

    const updatedOrder = {
      id: orderDetails.id,
      items: updatedItems,
      orderlySystem: OrderlySystem.OFS,
    };

    try {
      setLoading(true);
      const result = await client.updateOrder(selectedStore?.storeNumber, updatedOrder);
      if (result.success) {
        handleSnackbarOpen(t('orderCreatedSuccessfully'), 'success');
        await fetchOrderDetails(orderDetails.id);
      }
      else {
        setShowQuantityWarningDialog(true);
        setWarningMessage(result.message);
        fixQuantities();
      }
    } catch (error) {
      handleSnackbarOpen(t('saveError') + ' ' + error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = async () => {
    setLoading(true);
    await fetchItems({
      item: '',
      orderType: orderType,
      storeNumber: selectedStore?.storeNumber,
      categoryId: -1,
      supplier: supplier,
      pageNumber: 0,
      pageSize: 10,
      sort: 'Name',
      direction: 'asc',
    });
    setPage(0);
    setPageSize(10);
    setProductSearch('');
    setCategory(-1);
    setLoading(false);
  };

  const getFilterOptions = (resetPage: boolean) => {
    const filterOptions: ItemsFilterOptions = {
      storeNumber: selectedStore?.storeNumber,
      item: productSearch,
      orderType: orderType,
      categoryId: category,
      supplier: supplier,
      pageNumber: resetPage ? 0 : page,
      pageSize: pageSize,
      sort: orderBy,
      direction: order,
    };
    return filterOptions;
  };

  const handleAddItem = (item: Item, quantity: number) => {
    const newItem = {
      item: isParEnabled
        ? { ...item, calculatedParQuantity: calculateItemParQuantity(item) }
        : item,
      quantity: Number(quantity),
      isNew: true,
      maxQuantity: item.maxQuantity,
      minQuantity: item.minQuantity,
    };

    setAddedItems(prev => [...prev, newItem]);
    setItemQuantities(prev => ({ ...prev, [item.id]: quantity }));
  };

  const closeItemRemovalModal = () => {
    setShowItemRemovalModal(false);
    setItemDescToRemove(undefined);
    setItemIdToRemove(undefined);
  };

  const clearDraftQuantity = (itemId: number) => {
    setItemQuantities((prevQuantities) => {
      if (prevQuantities[itemId] == null) {
        return prevQuantities;
      }

      const newQuantities = { ...prevQuantities };
      delete newQuantities[itemId];
      return newQuantities;
    });
  };

  const removeItemLocally = (itemId: number | undefined) => {
    if (itemId === undefined) return;

    setAddedItems((currentItems) => currentItems.filter((addedItem) => addedItem.item.id !== itemId));
    setClearedParItemIds((prevItemIds) => {
      if (!prevItemIds.has(itemId)) {
        return prevItemIds;
      }

      const nextItemIds = new Set(prevItemIds);
      nextItemIds.delete(itemId);
      return nextItemIds;
    });
    setItemQuantities((prevQuantities) => {
      const newQuantities = { ...prevQuantities };
      delete newQuantities[itemId];
      return newQuantities;
    });
    clearDraftQuantityText(itemId);
  };
  const handleRemoveItem = (itemId: number | undefined) => {
    if (itemId === undefined) return;

    if (isParEnabled) {
      setItemQuantities(prev => ({ ...prev, [itemId]: 0 }));
    } else {
      setAddedItems(prev => prev.filter(x => x.item.id !== itemId));
      setItemQuantities(prev => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
    }

    setShowItemRemovalModal(false);
    setItemDescToRemove(undefined);
    setItemIdToRemove(undefined);
  };

  const handleQuantityChange = (itemId: number, quantity: number) => {
    if (!isParEnabled && quantity <= 0 && isItemInOrder(itemId)) {
      const item =
        items.find(x => x.id === itemId) ??
        addedItems.find(orderItem => orderItem.item.id === itemId)?.item;

      if (item) {
        onRemoveButtonClick(item.id, item.name);
      }
      return;
    }

    setItemQuantities(prev => ({ ...prev, [itemId]: quantity }));
  };

  const getAvailableItemQuantity = (item: Item): number | string => {
    if (draftQuantityText[item.id] != null) {
      return draftQuantityText[item.id];
    }

    if (itemQuantities[item.id] != null) {
      return itemQuantities[item.id];
    }

    if (isParEnabled) {
      const parQuantity = calculateItemParQuantity(item);
      if (parQuantity != null) {
        return parQuantity;
      }
    }

    return 1;
  };

  const getAddedItemQuantity = (orderItem: AddedItem): number | string => {
    if (draftQuantityText[orderItem.item.id] != null) {
      return draftQuantityText[orderItem.item.id];
    }

    if (clearedParItemIds.has(orderItem.item.id)) {
      const displayedParQuantity = orderItem.item.calculatedParQuantity ?? orderItem.item.parQuantityApplied;
      if (displayedParQuantity != null) {
        return displayedParQuantity;
      }
    }

    if (itemQuantities[orderItem.item.id] != null) {
      return itemQuantities[orderItem.item.id];
    }

    return orderItem.quantity;
  };

  // Blank field normalizes to the applicable PAR quantity, or 1 when no PAR quantity applies.
  const getAvailableItemEffectiveQuantity = (item: Item): number => {
    const quantity = getAvailableItemQuantity(item);
    if (quantity !== '') {
      return Number(quantity);
    }

    if (isParEnabled) {
      const parQuantity = calculateItemParQuantity(item);
      if (parQuantity != null) {
        return parQuantity;
      }
    }

    return 1;
  };

  const getAddedItemEffectiveQuantity = (orderItem: AddedItem): number => {
    const quantity = getAddedItemQuantity(orderItem);
    if (quantity !== '') {
      return Number(quantity);
    }

    if (isParEnabled) {
      const displayedParQuantity = orderItem.item.calculatedParQuantity ?? orderItem.item.parQuantityApplied;
      if (displayedParQuantity != null) {
        return displayedParQuantity;
      }
    }

    return 1;
  };

  const setDraftQuantity = (itemId: number, rawValue: string) => {
    setDraftQuantityText(prev => ({ ...prev, [itemId]: rawValue }));
  };

  const clearDraftQuantityText = (itemId: number) => {
    setDraftQuantityText((prev) => {
      if (prev[itemId] == null) {
        return prev;
      }

      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  };

  const handleAvailableItemQuantityInputChange = (item: Item, rawValue: string) => {
    setDraftQuantity(item.id, rawValue);

    if (rawValue === '') {
      return;
    }

    const parsedQuantity = Number(rawValue);
    if (Number.isNaN(parsedQuantity)) {
      return;
    }

    handleQuantityChange(item.id, Math.abs(parsedQuantity));
  };

  const handleAvailableItemQuantityBlur = (item: Item) => {
    // Field left empty: clear the override so calculated PAR stays authoritative, otherwise fall back to 1.
    if (draftQuantityText[item.id] === '') {
      const parQuantity = isParEnabled ? calculateItemParQuantity(item) : null;

      if (parQuantity != null) {
        clearDraftQuantity(item.id);
      } else {
        handleQuantityChange(item.id, 1);
      }
    }
    clearDraftQuantityText(item.id);
  };

  const handleAddedItemQuantityBlur = (orderItem: AddedItem) => {
    // Field left empty: PAR items restore to PAR quantity (or remove if none); otherwise normalize to 1.
    if (draftQuantityText[orderItem.item.id] === '') {
      if (isParEnabled) {
        const displayedParQuantity = orderItem.item.calculatedParQuantity ?? orderItem.item.parQuantityApplied;

        if (displayedParQuantity != null) {
          setClearedParItemIds((prevItemIds) => {
            const nextItemIds = new Set(prevItemIds);
            nextItemIds.add(orderItem.item.id);
            return nextItemIds;
          });
          clearDraftQuantity(orderItem.item.id);
          clearDraftQuantityText(orderItem.item.id);
          return;
        }

        removeItemLocally(orderItem.item.id);
        return;
      }

      handleQuantityChange(orderItem.item.id, getAddedItemEffectiveQuantity(orderItem));
    }
    clearDraftQuantityText(orderItem.item.id);
  };

  const handleAddedItemQuantityInputChange = (orderItem: AddedItem, rawValue: string) => {
    setDraftQuantity(orderItem.item.id, rawValue);

    if (rawValue === '') {
      return;
    }

    const parsedQuantity = Number(rawValue);
    if (Number.isNaN(parsedQuantity)) {
      return;
    }

    // A new explicit quantity supersedes any previously cleared PAR fallback marker.
    setClearedParItemIds((prevItemIds) => {
      if (!prevItemIds.has(orderItem.item.id)) {
        return prevItemIds;
      }

      const nextItemIds = new Set(prevItemIds);
      nextItemIds.delete(orderItem.item.id);
      return nextItemIds;
    });

    handleQuantityChange(orderItem.item.id, Math.abs(parsedQuantity));
  };


  const isItemInOrder = (itemId: number) => {
    return addedItems.some(orderItem => orderItem.item.id === itemId);
  };

  const disableUnavailableDates = (date: Dayjs) => {
    const today = dayjs();
    const dayjsDate = dayjs(date);

    if (isAutoShipOrder(orderType)) {
      return true;
    }

    if (orderKind === OrderKind.Emergency) {
      return dayjsDate.isBefore(today, 'day') || dayjsDate.isSame(today, 'day');
    }

    return !deliverySchedules.some((schedule) =>
      dayjsDate.isSame(dayjs(schedule.deliveryDate, 'DD-MM-YYYY'), 'day'),
    );
  };

  const handleDeliveryDateChange = async (newDate: Dayjs | null) => {
    setLoading(true);

    if (orderKind === OrderKind.Emergency) {
      setCutOffDate(dayjs());
    } else {
      const schedule = deliverySchedules.find((ds) =>
        dayjs(ds.deliveryDate, 'DD-MM-YYYY').isSame(dayjs(newDate), 'day'),
      );
      setCutOffDate(dayjs(schedule?.cutOffDate, 'DD-MM-YYYY'));
    }

    setDeliveryDate(newDate);

    if (orderKind === OrderKind.Forecast) {
      const formattedDate = dayjs(newDate).format('DD-MM-YYYY');
      const orderResponse = await client.getOrderByDeliveryDate(selectedStore?.storeNumber, orderType, formattedDate, supplier);

      if (orderResponse?.data) {
        setOrderDetails(orderResponse.data);

        const fetchedAddedItems = orderResponse.data.items.map(item => ({
          item: {
            id: item.itemId,
            sku: item.sku,
            name: item.name,
            categoryId: item.categoryId,
            category: item.category,
            stockOnHand: item.stockOnHand,
            stockOnOrder: item.stockOnOrder,
            price: item.price,
            primaryUom: item.primaryUom,
            orderableUom: item.orderableUom,
            barcode: '',
            barcodeUom: '',
            imageId: item.imageId,
            thumbnailUrl: item.thumbnailUrl,
            packSize: item.packSize,
            calculatedParQuantity: item.calculatedParQuantity,
            parQuantityApplied: item.parQuantityApplied,
            isQuantityOverridden: item.isQuantityOverridden,

          },
          quantity: item.quantity,
          maxQuantity: item.maxQuantity,
          minQuantity: item.minQuantity,
          isNew: false,
        }));

        setAddedItems(fetchedAddedItems);
        setClearedParItemIds(new Set());

        const newItemQuantities = orderResponse.data.items.reduce((acc, item) => ({
          ...acc,
          [item.itemId]: item.quantity,
        }), {});

        setItemQuantities(newItemQuantities);
      }
    }

    setLoading(false);
  };

  const handleOrderKindChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isEmergencyOrder = event.target.checked;
    if (isEmergencyOrder) {
      setOrderKind(OrderKind.Emergency);
      setCutOffDate(dayjs());
      setAddedItems([]);
      setClearedParItemIds(new Set());
      setItemQuantities({});
      setOrderDetails(null);
    } else {
      setOrderKind(OrderKind.Forecast);
      setDeliveryDate(null);
      setCutOffDate(null);
      setAddedItems([]);
      setClearedParItemIds(new Set());
      setItemQuantities({});
      setOrderDetails(null);
    }
  };

  const handleOrderTypeChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newOrderType = Number(event.target.value);
    setOrderType(newOrderType);
    setLoading(true);
    setAddedItems([]);
    setClearedParItemIds(new Set());
    setItemQuantities({});
    setDeliveryDate(null);
    setCutOffDate(null);
    setOrderDetails(null);
    setPage(0);
    setPageSize(10);
    setProductSearch('');
    setCategory(-1);
    const supplier = suppliers && suppliers.length > 0 && isOrderTypeDSD(newOrderType) ? suppliers[0].number : '';
    setSupplier(supplier);
    await fetchCategories(newOrderType, supplier);
    await fetchItems({
      item: '',
      orderType: newOrderType,
      storeNumber: selectedStore?.storeNumber,
      categoryId: -1,
      supplier: supplier,
      pageNumber: 0,
      pageSize: 15,
      sort: 'Name',
      direction: 'asc',
    });
    setLoading(false);
    await fetchDeliverySchedules(newOrderType, supplier);
  };

  const handleSupplierChange = async (number: string) => {
    setSupplier(number);
    setLoading(true);
    await fetchCategories(orderType, number);
    await fetchItems({
      item: productSearch,
      orderType: orderType,
      storeNumber: selectedStore?.storeNumber,
      categoryId: -1,
      supplier: number,
      pageNumber: page,
      pageSize: pageSize,
      sort: 'Name',
      direction: 'asc',
    });
    await fetchDeliverySchedules(orderType, number);
    setLoading(false);
  };

  const autocompleteOptions = [{ id: -1, name: t('all'), parentName: '' }, ...(Array.isArray(categories) ? categories : [])];

  const canSaveOrder = (): boolean => {
    return !loading && deliveryDate !== null && cutOffDate !== null && ((!orderDetails) || (orderDetails && orderDetails?.status == OrderStatus.Editable));
  };

  const canSubmitOrder = (): boolean => {
    return !loading && orderKind === OrderKind.Emergency && orderDetails !== null && orderDetails !== undefined
      && orderDetails.status === OrderStatus.Editable && addedItems.length > 0;
  };

  const handleSubmitOrder = async () => {
    if (!selectedStore || !orderDetails) {
      handleSnackbarOpen(t('submitError'), 'error');
      return;
    }

    await handleUpdate();

    try {
      setLoading(true);
      const result = await client.submitOrder(selectedStore.storeNumber, orderDetails.id.toString(), orderDetails);
      if (result.success) {
        handleSnackbarOpen(t('submitSuccess'), 'success');
        await fetchOrderDetails(orderDetails.id);
      } else {
        handleSnackbarOpen(t('submitFail'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('submitFail'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const getPageTitle = (): string => {
    if (orderDetails) {
      return `${t('order')}: ${orderDetails.id}`;
    } else {
      return orderKind === OrderKind.Forecast ? t('selectDeliveryDateToBegin') : t('newOrder');
    }
  };

  const handleExitAndSave = async () => {
    if (!orderDetails && orderKind === OrderKind.Emergency && canSaveOrder()) {
      // For new emergency orders, create and navigate away directly
      if (selectedStore?.storeNumber && deliveryDate && cutOffDate && orderType !== -1) {
        const itemsToSave = isParEnabled
          ? addedItems.filter(p => !clearedParItemIds.has(p.item.id))
          : addedItems;
        const createOrderRequest = {
          storeNumber: selectedStore.storeNumber,
          emergencyOrder: true,
          orderTypeId: orderType,
          deliveryDate: deliveryDate.format('YYYY-MM-DD'),
          cutOffDate: cutOffDate.format('YYYY-MM-DD'),
          supplierNumber: supplier,
          orderlySystem: OrderlySystem.OFS,
          items: itemsToSave.map(product => ({
            id: product.item.id,
            sku: product.item.sku,
            quantity: itemQuantities[product.item.id] ?? product.quantity ?? 1,
            predictedQuantity: predictedDemands.find(x => x.itemNumber === getInventorySku(product.item))
              ?.predictedDemandItems?.find(x => deliveryDate?.isSame(x.date, 'day'))?.quantity,
          })),
        };
        try {
          setLoading(true);
          await client.createOrder(createOrderRequest);
        } catch (error) {
          // proceed with navigation regardless
        } finally {
          setLoading(false);
        }
      }
    } else if (canSaveOrder()) {
      await handleSave();
    }
    navigate('/store/orders-history');
  };

  const handleSnackbarOpen = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const cutOffTimeLeftFormat = (cutOffTimeLeft: CutOffTimeLeft) => {
    if (!cutOffTimeLeft ||
      cutOffTimeLeft?.days == undefined ||
      !cutOffTimeLeft?.minutes == undefined ||
      !cutOffTimeLeft?.hours == undefined ||
      (cutOffTimeLeft?.days === 0 && cutOffTimeLeft?.hours === 0 && cutOffTimeLeft?.minutes === 0)) {
      return `${t('cutOffHasPassed')}`;
    }

    const daysString = cutOffTimeLeft?.days > 0 ? `${cutOffTimeLeft.days} ${t('days')}, ` : '';
    const hoursString = cutOffTimeLeft?.hours > 0 ? `${cutOffTimeLeft.hours} ${t('hours')}, ` : '';
    const minutesString = `${cutOffTimeLeft?.minutes} ${t('minutes')}`;

    return `${daysString}${hoursString}${minutesString}`;
  };

  const handleShowPredictedQuantityChange = (event: ChangeEvent<HTMLInputElement>) => {
    setShowPredictedQuantity(event.target.checked);
  };

  const matchPredictedQuantities = (resetMatchedPages = false) => {
    setItemQuantities(matchQuantities);

    if (!isMatchPredictedQuantitiesActive) {
      setIsMatchPredictedQuantitiesActive(true);
    }

    if (resetMatchedPages) {
      setMatchedPredictionPages([page]);
    }
  };

  const matchQuantities = (oldQuantities: Record<number, number>) => {
    const newQuantities = JSON.parse(JSON.stringify(oldQuantities)) as Record<number, number>;
    const allItems = Array.from(new Set([...items, ...addedItems.map(x => x.item)]));
    allItems.forEach((item) => {
      const predictedItem = predictedDemands.find(p => p.itemNumber === getInventorySku(item));
      const predictedDemandItem = predictedItem?.predictedDemandItems?.find(x => deliveryDate?.isSame(x.date, 'day'));

      if (!predictedDemandItem?.hasPrediction) {
        return;
      }

      newQuantities[item.id] = predictedDemandItem.quantity;
    });

    return newQuantities;
  };


  const getOptionLabel = (option: { parentName?: string; name: string }): string => {
    const parentName = option.parentName;
    const categoryName = option.name;

    const translatedParent = parentName && isOrderCategoryParent(parentName)
      ? t(OrdersCategoriesParentsMap[parentName as keyof typeof OrdersCategoriesParentsMap])
      : parentName;

    const translatedCategory = isOrderCategory(categoryName)
      ? t(OrderCategoriesTranslationMap[categoryName as keyof typeof OrderCategoriesTranslationMap])
      : categoryName;

    if (parentName) {
      return translatedParent
        ? `${translatedParent} > ${translatedCategory}`
        : `${parentName} > ${translatedCategory}`;
    } else {
      return translatedCategory;
    }
  };

  const quantityError = (item: AddedItem | Item, currentValue: number): string | null => {
    const quantity = Number(currentValue);

    if (isParEnabled && quantity === 0) {
      return null;
    }

    if ((item.maxQuantity != null) && quantity > item.maxQuantity) {
      return `${t('maxQuantityAllowedIs')}: ${item.maxQuantity}`;
    }

    if ((item.minQuantity != null) && quantity < item.minQuantity)
      return `${t('minQuantityAllowedIs')}: ${item.minQuantity}`;

    return null;
  };

  const isQuantityError = (item: AddedItem | Item, currentValue: number): boolean => {
    return quantityError(item, currentValue) != null;
  };

  const anyErrors = () => {
    return addedItems.some((orderItem) => isQuantityError(orderItem, getAddedItemEffectiveQuantity(orderItem)));
  };

  const fixQuantities = () => {
    //shallow copy for state mang purposes
    const itemQuantities2 = { ...itemQuantities };
    for (const key in itemQuantities2) {
      const value = itemQuantities2[key];
      const maxQuant = orderDetails?.items.find(x => x.itemId.toString() == key)?.maxQuantity;
      const minQuant = orderDetails?.items.find(x => x.itemId.toString() == key)?.minQuantity;

      if (isParEnabled && value === 0) {
        continue;
      }

      if (maxQuant && value > maxQuant)
        itemQuantities2[key] = maxQuant;
      if (minQuant && value < minQuant)
        itemQuantities2[key] = minQuant;
    }
    setItemQuantities(itemQuantities2);
  };

  const onRemoveButtonClick = (itemId: number, itemDesc: string) => {
    setItemIdToRemove(itemId);
    setItemDescToRemove(itemDesc);
    setShowItemRemovalModal(true);
  };

  const orderTotal = useMemo(() => {
    return addedItems.reduce((total, orderItem) => {
      const qty = itemQuantities[orderItem.item.id] ?? orderItem.quantity;
      return total + (qty * (orderItem.item.price ?? 0));
    }, 0);
  }, [addedItems, itemQuantities]);

  return (
    <PageArea>
      <Grid item
        mb={10}
        sx={{ textAlign: 'left' }}>
        <BackButton
          handleClick={handleExitAndSave}
          title={t('exitAndSave')}
          isLink={false}
        />
      </Grid>
      <Grid container>
        <Grid item
          container
          justifyContent={'space-between'}
          md={12}>
          <Grid item
            xs={8}
            md={10}>
            <PageTitle>
              {getPageTitle()}
            </PageTitle>
          </Grid>
          <Grid item
            xs={4}
            md={2}
            sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="primary"
              disabled={!canSaveOrder() || anyErrors()}
              size="lg"
              startIcon={<SaveIcon />}
              onClick={() => handleSave()}>
              {t('save')}
            </Button>
            {orderKind === OrderKind.Emergency && (
              <Button
                variant="primary"
                disabled={!canSubmitOrder() || anyErrors()}
                size="lg"
                onClick={() => handleSubmitOrder()}>
                {t('submitOrder')}
              </Button>
            )}
          </Grid>
          {orderDetails && orderKind === OrderKind.Forecast && <Grid item
            xs={12}
            md={5}> {<Typography
              variant='textMD'>{t('timeToOrderCutoff')}  <strong>{cutOffTimeLeftFormat(orderDetails?.cutOffTimeLeft)}</strong></Typography>}
          </Grid>}
        </Grid>
        <Grid item
          container
          mt={5}
          sm={12}
          sx={{
            pt: '20px',
            border: '1px solid',
            borderColor: theme.palette.custom.gray[300],
          }}>
          <Grid item
            container
            alignItems={'center'}
            xs={12}>
            <Grid item
              xl={4}
              md={isMobile ? 12 : 7}
              xs={isMobile ? 12 : 6}
              container
              direction="column"
              justifyContent="center"
              alignItems="center"
              sx={{ padding: 0 }} >
              <Box>
                <DatePickerLocalized
                  label=''
                  useCalendar={true}
                  valueForDateCalendar={deliveryDate}
                  onChangeDateCalendar={handleDeliveryDateChange}
                  shouldDisableDateCalendar={disableUnavailableDates}
                  disabled={loading || isExistingOrder}
                  disableHighlightToday={true}
                />
              </Box>
            </Grid>
            <Grid item
              container
              alignItems={'center'}
              xl={8}
              md={isMobile ? 12 : 5}
              xs={isMobile ? 12 : 6}>
              <Grid item
                xl={isOrderTypeDSD(orderDetails?.orderTypeId ?? -1) && !isMobile ? 4 : 6}
                md={isOrderTypeDSD(orderDetails?.orderTypeId ?? -1) && !isMobile ? 6 : 12}
                xs={12}
                p={3}>
                <TextField
                  fullWidth
                  autoComplete='off'
                  size='small'
                  label={t('orderType')}
                  disabled={loading || isExistingOrder}
                  value={orderType}
                  required
                  onChange={handleOrderTypeChange}
                  select
                >
                  {orderTypes.map((type) => (
                    <MenuItem key={type.id}
                      value={type.id}>
                      {t(OrderTypeTranslationMap[type.name as keyof typeof OrderTypeTranslationMap]) || type.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {isOrderTypeDSD(orderType) &&
                <Grid item
                  xl={4}
                  md={!isMobile ? 6 : 12}
                  xs={12}
                  p={3}>
                  <TextField
                    fullWidth
                    size='small'
                    autoComplete='off'
                    label={t('supplier')}
                    disabled={loading || isExistingOrder}
                    value={supplier}
                    required
                    onChange={(e) => handleSupplierChange(e.target.value)}
                    select
                  >
                    {suppliers.map((type) => (
                      <MenuItem key={type.id}
                        value={type.number}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              }
              <Grid item
                xl={isOrderTypeDSD(orderDetails?.orderTypeId ?? -1) ? 4 : 6}
                md={12}
                p={3}>
                <Typography component="div">
                  <Grid component="label"
                    container
                    alignItems="center"
                    spacing={1}>
                    <Grid item>{t('emergencyOrder')}</Grid>
                    <Grid item>
                      <Switch
                        disabled={loading || !hasPermissionTo(['OrderAdHocOrders']) || isExistingOrder}
                        checked={orderKind === OrderKind.Emergency}
                        onChange={handleOrderKindChange}
                        name="orderKindSwitch"
                      />
                    </Grid>
                  </Grid>
                </Typography>
              </Grid>
              <Grid item
                md={6}
                xs={12}
                p={3}>
                <DateField
                  label={t('deliveryDate')}
                  disablePast={true}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  readOnly
                  value={deliveryDate}
                />
              </Grid>
              <Grid item
                md={6}
                xs={12}
                p={3}>
                <DateField
                  label={t('cutOffDate')}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  readOnly
                  value={cutOffDate}
                />
              </Grid>
              <Grid item
                md={6}
                xs={12}
                p={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('orderTotal')}
                  disabled
                  value={applyRoundAndFormat(orderTotal, currencyCode)}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item
            xs={12}
            mt={5}
            mb={5}>
            <Divider />
          </Grid>
          <Grid item
            container
            xs={12}
            spacing={5}>
            <Grid item
              container
              xs={12}>
              <Grid item
                lg={3}
                xs={12}
                p={5}>
                <TextField
                  fullWidth
                  size='small'
                  sx={{ mb: 0 }}
                  placeholder={t('searchByItemNameOrSKU')}
                  disabled={loading}
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SvgIcon><SearchSm /></SvgIcon>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item
                lg={3}
                xs={12}
                p={5}>
                <Autocomplete
                  size='small'
                  disabled={loading}
                  sx={{ mb: 0 }}
                  options={autocompleteOptions}
                  getOptionLabel={getOptionLabel}
                  value={categories.find(cat => cat.id === category) ?? autocompleteOptions[0]}
                  onChange={(event, newValue) => {
                    setCategory(newValue ? newValue.id : -1);
                  }}
                  renderInput={(params) => (
                    <TextField {...params}
                      sx={{ mb: 0 }}
                      label={t('category')}
                      placeholder={t('choose')} />
                  )}
                />
              </Grid>
              <Grid item
                lg={3}
                xs={12}
                p={5}>
                <Button fullWidth
                  variant="secondary"
                  disabled={loading}
                  size="lg"
                  onClick={handleClearFilters}>
                  {t('clearFilters')}
                </Button>
              </Grid>
              <Grid item
                lg={3}
                xs={12}
                p={5}>
                <Button fullWidth
                  variant="primary"
                  disabled={loading}
                  size="lg"
                  startIcon={<SearchSm />}
                  onClick={handleSearch}>
                  {t('search')}
                </Button>
              </Grid>
              {
                isPodEnabled && canEditPredictions && predictedDemands.length > 0 &&
                <>
                  <Grid item
                    xs={12}
                    lg={3}
                    p={5}>
                    <FormControlLabel
                      control={<Switch checked={showPredictedQuantity}
                        onChange={handleShowPredictedQuantityChange}
                        disabled={isPodPredictionReadonlyEnabled && !canEditPredictions} />}
                      label={t('showPredictedQuantities')} />
                  </Grid>
                  {
                    showPredictedQuantity &&
                    <Grid item
                      xs={12}
                      lg={3}
                      p={5}>
                      <Button fullWidth
                        variant="primary"
                        disabled={loading}
                        size="lg"
                        startIcon={<MagicWand01 />}
                        onClick={() => matchPredictedQuantities(true)}>
                        {t('matchPredictedQuantities')}
                      </Button>
                    </Grid>
                  }
                </>
              }
            </Grid>
            {isParEnabled && (
              <Grid item
                xs={12}
                px={4}>
                <Alert severity="info"
                  sx={{ mb: 4 }}>
                  {t('parInfoBanner')}
                </Alert>
              </Grid>
            )}
            <Grid item
              lg={6}
              xs={12}>
              <Typography px={4}
                variant="displayXS">{t('availableItems')}</Typography>
              {loading ? <LoadingWheel /> : <TableContainer component={'div'}>
                <Table
                  size="small"
                  aria-label="a table"
                >
                  <TableHead
                    sx={{
                      paddingTop: '10px',
                      backgroundColor: theme.palette.custom.gray[200],
                    }}>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: 'bold' }}
                        align="left">
                        <TableSortLabel
                          active={orderBy === 'SKU'}
                          direction={order}
                          onClick={() => handleSort('SKU')}>
                          {t('sku')}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 'bold' }}
                        align="center">
                        {t('quantity')}
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 'bold' }}
                        align="center">
                        {t('actions')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item) => {
                      const quantity = getAvailableItemQuantity(item);
                      const predictedItem = predictedDemands.find(x => x.itemNumber === getInventorySku(item));
                      const predictedDemandItem = predictedItem?.predictedDemandItems?.find(x => deliveryDate?.isSame(x.date, 'day'));
                      const showPrediction = showPredictedQuantity && predictedDemandItem?.hasPrediction;
                      const predictedQuantity = predictedDemandItem?.quantity;
                      const borderColor = showPrediction && canEditPredictions && predictedQuantity === quantity ? theme.palette.success[400] : '';
                      const safetyStock = predictedDemandItem?.safetyStock;
                      const predictedDemandQuantity = predictedDemandItem?.predictedDemandQuantity;
                      const parQuantity = isParEnabled ? calculateItemParQuantity(item) : null;
                      const isParDefaultQuantity = parQuantity != null && quantity === parQuantity;
                      const canAddItem = !isItemInOrder(item.id) && canSaveOrder() && (isParEnabled || getAvailableItemEffectiveQuantity(item) > 0);

                      return (
                        <TableRow key={item.id}>
                          <TableCell><ProductCard item={item}
                            currencyCode={currencyCode} /></TableCell>
                          <TableCell>
                            <Box className="product-quantity-container"
                              sx={{ display: 'flex', position: 'relative', maxWidth: 100 }}>
                              <TextField
                                label={(showPrediction && canEditPredictions) ? t('predictedQuantity', { value: predictedQuantity }) : isParEnabled && isParDefaultQuantity ? t('parLabel') : null}
                                size="small"
                                type="number"
                                autoComplete='off'
                                disabled={isItemInOrder(item.id) || (showPrediction && !canEditPredictions)}
                                value={quantity}
                                onChange={(e) => handleAvailableItemQuantityInputChange(item, e.target.value)}
                                onBlur={() => handleAvailableItemQuantityBlur(item)}
                                inputProps={{ min: 0 }}
                                sx={{
                                  mb: 0,
                                  maxWidth: 100,
                                  width: '100%',
                                  input: {
                                    textAlign: 'center',
                                  },
                                  '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                      borderColor: borderColor,
                                    },
                                  },
                                  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                    display: (showPrediction && !canEditPredictions) ? 'none' : undefined,
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: borderColor,
                                  },
                                }}
                              ></TextField>
                              {
                                showPrediction && canEditPredictions &&
                                <Tooltip title={
                                  <span>
                                    {
                                      predictedDemandQuantity != null && (
                                        <>
                                          {t('predictedDemandQuantity', { value: predictedDemandQuantity, uom: item.primaryUom })}
                                          <br />
                                        </>
                                      )
                                    }
                                    {safetyStock != null && t('safetyStock', { value: safetyStock, uom: item.primaryUom })}
                                  </span>
                                }>
                                  <IconButton aria-label={t('applyPredictedQuantity')}
                                    onClick={() => handleQuantityChange(item.id, Number(predictedQuantity))}>
                                    <MagicWand01
                                      color={predictedQuantity === quantity ? theme.palette.success[700] : theme.palette.custom.gray[400]} />
                                  </IconButton>
                                </Tooltip>
                              }
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Button
                              onClick={() => handleAddItem(item, getAvailableItemEffectiveQuantity(item))}
                              variant="primary"
                              disabled={!canAddItem}
                              size="md"
                              startIcon={isItemInOrder(item.id) ? <DoneIcon /> : <Plus />}>
                              {isItemInOrder(item.id) ? t('added') : t('add')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {!loading && items.length === 0 ? (
                  <Box
                    sx={{
                      py: '20px',
                      px: '16px',
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      variant="textLG"
                      sx={{
                        textAlign: 'center',
                      }}
                    >
                      {t('noItems')}
                    </Typography>
                  </Box>
                ) : (
                  <CustomPagination
                    page={page}
                    setPage={setPage}
                    maxPages={totalPages}
                    breakpointForChangeDisplay={120000}
                  />
                )}
              </TableContainer>
              }
            </Grid>
            <Grid item
              lg={6}
              xs={12}>
              <Typography px={4}
                variant="displayXS">{t('addedItems')}</Typography>
              <TableContainer component={'div'}>
                <Table
                  size="small"
                  aria-label="a table"
                >
                  <TableHead
                    sx={{
                      paddingTop: '10px',
                      backgroundColor: theme.palette.custom.gray[200],
                    }}>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: 'bold' }}
                        align="left">
                        {t('sku')}
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 'bold' }}
                        align="center">
                        {t('quantity')}
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 'bold' }}
                        align="center">
                        {t('actions')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {addedItems.map((orderItem) => {
                      const quantity = getAddedItemQuantity(orderItem);
                      const predictedItem = predictedDemands.find(x => x.itemNumber === getInventorySku(orderItem.item));
                      const predictedDemandItem = predictedItem?.predictedDemandItems?.find(x => deliveryDate?.isSame(x.date, 'day'));
                      const showPrediction = showPredictedQuantity && predictedDemandItem?.hasPrediction;
                      const predictedQuantity = predictedDemandItem?.quantity;
                      const borderColor = showPrediction && canEditPredictions && predictedQuantity === quantity ? theme.palette.success[400] : '';
                      const safetyStock = predictedDemandItem?.safetyStock;
                      const predictedDemandQuantity = predictedDemandItem?.predictedDemandQuantity;
                      const displayedParQuantity = orderItem.item.calculatedParQuantity ?? orderItem.item.parQuantityApplied;
                      const isParOwnedItem = isParEnabled
                        && orderItem.item.isQuantityOverridden !== true
                        && displayedParQuantity != null
                        && quantity === displayedParQuantity;
                      const removeFromOrderTitle = t(isParEnabled ? 'doNotOrderModalTitle' : 'deleteModalTitle');

                      return (
                        <TableRow key={orderItem.item.id}>
                          <TableCell><ProductCard item={orderItem.item}
                            currencyCode={currencyCode} /></TableCell>
                          <TableCell sx={{ overflow: 'hidden' }}>
                            <Box className="product-quantity-container"
                              sx={{ display: 'flex', position: 'relative', maxWidth: 100 }}>
                              <TextField
                                label={(showPrediction && canEditPredictions) ? t('predictedQuantity', { value: predictedQuantity }) : isParEnabled && isParOwnedItem ? t('parLabel') : null}
                                error={isQuantityError(orderItem, itemQuantities[orderItem.item.id])}
                                helperText={quantityError(orderItem, itemQuantities[orderItem.item.id])}
                                size="small"
                                type="number"
                                autoComplete='off'
                                disabled={!canSaveOrder() || (showPrediction && !canEditPredictions)}
                                value={quantity}
                                onChange={(e) => handleAddedItemQuantityInputChange(orderItem, e.target.value)}
                                onBlur={() => handleAddedItemQuantityBlur(orderItem)}
                                inputProps={{ min: 0 }}
                                sx={{
                                  mb: 0,
                                  maxWidth: 100,
                                  width: '100%',
                                  input: {
                                    textAlign: 'center',
                                  },
                                  '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                      borderColor: borderColor,
                                    },
                                  },
                                  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                    display: (showPrediction && !canEditPredictions) ? 'none' : undefined,
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: borderColor,
                                  },
                                }}
                              ></TextField>
                              {
                                showPrediction && canEditPredictions &&
                                <Tooltip title={
                                  <span>
                                    {
                                      predictedDemandQuantity != null && (
                                        <>
                                          {t('predictedDemandQuantity', { value: predictedDemandQuantity, uom: orderItem.item.primaryUom })}
                                          <br />
                                        </>
                                      )
                                    }
                                    {safetyStock != null && t('safetyStock', { value: safetyStock, uom: orderItem.item.primaryUom })}
                                  </span>
                                }>
                                  <IconButton aria-label={t('applyPredictedQuantity')}
                                    onClick={() => handleQuantityChange(orderItem.item.id, Number(predictedQuantity))}>
                                    <MagicWand01
                                      color={predictedQuantity === quantity ? theme.palette.success[700] : theme.palette.custom.gray[400]} />
                                  </IconButton>
                                </Tooltip>
                              }
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={removeFromOrderTitle}>
                              <Box component="span"
                                sx={{ display: 'block', width: '100%' }}>
                                <Button
                                  aria-label={removeFromOrderTitle}
                                  fullWidth
                                  variant="bad"
                                  size="md"
                                  disabled={!canSaveOrder()}
                                  startIcon={<DeleteIcon />}
                                  onClick={() => onRemoveButtonClick(orderItem.item.id, orderItem.item.name)}>
                                  {t('remove')}
                                </Button>
                              </Box>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {addedItems.length === 0 ? (
                  <Box
                    sx={{
                      py: '20px',
                      px: '16px',
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      variant="textLG"
                      sx={{
                        textAlign: 'center',
                      }}
                    >
                      {t('noItemsAdded')}
                    </Typography>
                  </Box>
                ) : ('')}
              </TableContainer>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Snackbar open={snackbar.open}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}>
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <QuantityWarningDialog
        setShowQuantityWarningDialog={setShowQuantityWarningDialog}
        message={warningMessage}
        showDialog={showQuantityWarningDialog} />
      <RemoveItemConfirm
        onConfirm={() => handleRemoveItem(itemIdToRemove)}
        onClose={closeItemRemovalModal}
        open={showItemRemovalModal}
        name={itemDescToRemove}
        title={t(isParEnabled ? 'doNotOrderModalTitle' : 'deleteModalTitle')}
        message={t(isParEnabled ? 'doNotOrderModalMessage' : 'deleteModalMessage')}
        confirmLabel={t('remove')}
        confirmVariant="bad"
      />
    </PageArea>
  );
};

export default QuickOrderPage;
